import PropTypes from 'prop-types';
import { useEffect } from 'react';
// next
import { useRouter } from 'next/router';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, Drawer, List, ListSubheader } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useCompany from '../../../hooks/useCompany';
// config
import { NAVBAR } from '../../../config';
// components
import DIOText from '../../../components/DIOText';
import Logo from '../../../components/Logo';
import Scrollbar from '../../../components/Scrollbar';
import SvgIconStyle from '../../../components/SvgIconStyle';
import { NavSectionVertical } from '../../../components/nav-section';
import { ListItemStyle, ListItemIconStyle, ListItemTextStyle } from '../../../components/nav-section/vertical/style';
import navConfig from './NavConfig';
import NavbarAccount from './NavbarAccount';
// routes
import { PATH_COMPANY, PATH_TIME, PATH_FAQ } from '../../../routes/paths';
import Iconify from '../../../components/Iconify';

// ----------------------------------------------------------------------

export const ListSubheaderStyle = styled((props) => <ListSubheader disableSticky disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.overline,
    color: theme.palette.text.primary,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
  })
);

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const { company, activeCompany } = useCompany();
  const { pathname, push } = useRouter();

  const isDesktop = useResponsive('up', 'lg');
  const smUp = useResponsive('up', 'sm');

  const generateCompanyMenuData = (company, activeCompany) => {
    if (!company) return [];
    if (company.length === 0) return [];

    const header = {
      title: '계약 중인 회사',
      path: PATH_COMPANY.root,
    }
    const items = activeCompany.map((company) => {
      return {
        id: company.id,
        name: company.name,
        contract_hours: company.contract_hours,
        path: `${PATH_TIME.root}?c=${company.id}`,
      }
    });

    return [
      {
        header,
        items,
      }
    ]
  };

  const companyData = generateCompanyMenuData(company, activeCompany);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <>
    <Scrollbar
      sx={{
        height: smUp ? 'calc(100vh - 190px)' : 'calc(100vh - 160px)',
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Logo />
        </Stack>

        <NavbarAccount />
      </Stack>

      <NavSectionVertical company={companyData} navConfig={navConfig} />

      <Box sx={{ flexGrow: 1 }} />

    </Scrollbar>
    <Box sx={{ position: 'fixed', bottom: 0, width: 'inherit', px: 2, pb: { xs: 1, sm: 3 } }}>
      <List disablePadding>
        <Stack sx={{ px: 2, pb: 1 }}>
          <ListSubheaderStyle sx={{ color: "text.secondary" }}>
            도움말
          </ListSubheaderStyle>
        </Stack>

        <ListItemStyle onClick={() => window.open("https://dio.oopy.io/crew-guide", '_blank', 'noopener, noreferrer')}>
          <ListItemIconStyle>
            <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ width: 1, height: 1 }} />
          </ListItemIconStyle>
          <ListItemTextStyle disableTypography primary={'크루 가이드'} />
        </ListItemStyle>
        <ListItemStyle onClick={() => push(PATH_FAQ.root)} sx={{ ...pathname === "/faq" && { fontFamily: (theme) => theme.typography.subtitle2, backgroundColor: "grey.200" } }}>
          <ListItemIconStyle>
            <Iconify icon="eva:question-mark-circle-fill" />
          </ListItemIconStyle>
          <ListItemTextStyle disableTypography primary={'자주 묻는 질문'} />
        </ListItemStyle>
      </List>
    </Box>
    </>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: NAVBAR.DASHBOARD_WIDTH,
        },
      }}
    >
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar} PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}>
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: 'dashed',
              bgcolor: 'background.default',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
